import { supabase } from "@/integrations/supabase/client";
import { PaginationParams, PaginatedResponse } from "@/types";
import { Database, Tag, RecordWithTags } from "@/integrations/supabase/types";

type Record = Database["public"]["Tables"]["records"]["Row"];
type Template = Database["public"]["Tables"]["templates"]["Row"];

export interface RecordWithTemplate {
  id: string;
  name: string;
  status: string;
  processingStatus: string;
  processing_status_desc?: string;
  duration: unknown;
  filePath: string;
  createdAt: string;
  userId: string;
  templateId: string;
  processingStartedAt: string;
  processingFinishedAt: string;
  processingTime: string;
  processingResults: string;
  transcriptText: string;
  postProcessingText: string;
  deletedAt: string | null;
  tags?: Tag[]; // Optional tags array
  isPublished?: boolean; // Optional published status
  shareUrl?: string; // Optional share URL
  template: {
    id: string;
    name: string;
    description: string;
    prompt: string;
    isSystem: boolean;
    createdAt: string;
    userId: string;
    deletedAt: string | null;
    isDeleted?: boolean; // Добавляем флаг что шаблон удален
  } | null;
}

// Query Builder for optimized Supabase queries
class RecordQueryBuilder {
  private baseQuery: ReturnType<typeof supabase.from>;

  constructor() {
    this.baseQuery = supabase.from("records");
  }

  build(params: PaginationParams, includeTags: boolean = false) {
    // Include tags and shared_links in select if requested
    const selectQuery = includeTags
      ? "*, templates!left(*), record_tags(tags(*)), shared_links!left(id, token, is_active)"
      : "*, templates!left(*), shared_links!left(id, token, is_active)";

    let query = this.baseQuery
      .select(selectQuery, { count: "exact" })
      .is("deleted_at", null);

    // Apply user filter (required for RLS)
    if (params.userId) {
      query = query.eq("user_id", params.userId);
    }

    // Apply search filter
    if (params.search && params.search.trim()) {
      query = query.ilike("name", `%${params.search.trim()}%`);
    }

    // Apply status filter
    if (params.statusFilter && params.statusFilter !== "all") {
      query = query.eq("processing_status", params.statusFilter);
    }

    // Tag filter is now handled in execute() method to avoid complex subquery issues

    // Apply sorting
    const sortMap = {
      newest: { column: "created_at", ascending: false },
      oldest: { column: "created_at", ascending: true },
      "name-asc": { column: "name", ascending: true },
      "name-desc": { column: "name", ascending: false },
    };

    const sort =
      sortMap[params.sortBy as keyof typeof sortMap] || sortMap.newest;
    query = query.order(sort.column, { ascending: sort.ascending });

    // Apply pagination
    const from = (params.page - 1) * params.limit;
    const to = from + params.limit - 1;
    query = query.range(from, to);

    return query;
  }

  async execute(params: PaginationParams, includeTags: boolean = false) {
    console.log("Query Builder: building query with params", params);

    // Handle tag filtering separately to avoid complex subquery issues
    let recordIds: string[] | undefined;
    if (params.tagFilter && Array.isArray(params.tagFilter) && params.tagFilter.length > 0) {
      console.log("Query Builder: fetching record IDs with tags", params.tagFilter);

      const { data: taggedRecords, error: tagError } = await supabase
        .from("record_tags")
        .select("record_id")
        .in("tag_id", params.tagFilter);

      if (tagError) {
        console.error("Query Builder: Tag filter error", tagError);
        throw tagError;
      }

      recordIds = taggedRecords?.map(r => r.record_id) || [];
      console.log("Query Builder: found record IDs with tags", recordIds);

      if (recordIds.length === 0) {
        // No records found with these tags, return empty result
        console.log("Query Builder: no records found with specified tags");
        return { data: [], count: 0 };
      }
    }

    // Build query without tag filter (handled above)
    const paramsWithoutTagFilter = { ...params, tagFilter: undefined };
    let query = this.build(paramsWithoutTagFilter, includeTags);

    // Apply record ID filter if we have tag filtering
    if (recordIds && recordIds.length > 0) {
      query = query.in("id", recordIds);
    }

    console.log("Query Builder: executing query...");

    const { data, error, count } = await query;

    console.log("Query Builder: result", { data, error, count });

    if (error) {
      console.error("Query Builder: Supabase error", error);
      throw error;
    }

    return { data, count };
  }
}

// Transform raw Supabase data to application format
function transformRecord(
  record: Record & {
    templates: Template | null;
    record_tags?: Array<{ tags: Tag | null }>;
    shared_links?: Array<{ id: string; token: string; is_active: boolean }>;
  }
): RecordWithTemplate {
  // Extract tags from record_tags relation
  const tags = record.record_tags
    ?.map(rt => rt.tags)
    .filter(Boolean) as Tag[] || [];

  // Extract shared links information
  const activeSharedLink = record.shared_links?.find(link => link.is_active);
  const isPublished = !!activeSharedLink;
  const shareUrl = activeSharedLink
    ? `${window.location.origin}/shared/${activeSharedLink.token}`
    : undefined;

  return {
    id: record.id,
    name: record.name,
    status: record.status,
    processingStatus: record.processing_status,
    processing_status_desc: record.processing_status_desc || undefined,
    duration: record.duration,
    filePath: record.file_path,
    createdAt: record.created_at,
    userId: record.user_id,
    templateId: record.template_id,
    processingStartedAt: record.processing_started_at,
    processingFinishedAt: record.processing_finished_at,
    processingTime: String(record.processing_time || ''),
    processingResults: record.processing_results ? JSON.stringify(record.processing_results) : '',
    transcriptText: record.transcript_text,
    postProcessingText: record.post_processing_text,
    deletedAt: (record as { deleted_at?: string }).deleted_at || null,
    tags: tags.length > 0 ? tags : undefined, // Include tags if present
    isPublished, // Include published status
    shareUrl, // Include share URL if published
    template: record.templates
      ? {
        id: record.templates.id,
        name: record.templates.name,
        description: record.templates.description,
        prompt: record.templates.prompt,
        isSystem: record.templates.is_system,
        createdAt: record.templates.created_at,
        userId: record.templates.user_id,
        deletedAt: record.templates.deleted_at,
        isDeleted: !!record.templates.deleted_at,
      }
      : null,
  };
}

export const recordService = {
  async reprocessRecord(
    recordId: string,
    templateId: string
  ): Promise<{
    success: boolean;
    message: string;
    processed_text: string;
  }> {
    // Получаем текущую сессию пользователя
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      throw new Error("errors.unauthorized");
    }

    const reprocessUrl = import.meta.env.VITE_REPROCESS_URL;
    if (!reprocessUrl) {
      throw new Error("errors.record.reprocess_config_missing");
    }

    const response = await fetch(reprocessUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        record_id: recordId,
        template_id: templateId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Ошибка ${response.status}: ${response.statusText}`
      );
    }

    return await response.json();
  },

  // New method for paginated records fetching
  async getPaginatedRecords(
    params: PaginationParams,
    includeTags: boolean = false
  ): Promise<PaginatedResponse<RecordWithTemplate>> {
    console.log('getPaginatedRecords called with params:', params, 'includeTags:', includeTags);

    // Critical: userId is required for RLS policies
    if (!params.userId) {
      console.error('getPaginatedRecords: userId is missing!');
      throw new Error('userId is required for fetching records');
    }

    const builder = new RecordQueryBuilder();

    console.log('getPaginatedRecords: executing query...');

    const { data, count } = await builder.execute(params, includeTags);

    console.log('getPaginatedRecords: query completed', { dataLength: data?.length, count });

    if (!data || count === null) {
      console.error('getPaginatedRecords: invalid data or count', { data, count });
      throw new Error('errors.record.load_error');
    }

    // Transform data to application format
    const transformedData = data.map(transformRecord);

    console.log('getPaginatedRecords: data transformed', { transformedDataLength: transformedData.length });

    // Calculate pagination metadata
    const totalPages = Math.ceil(count / params.limit);

    const result = {
      data: transformedData,
      pagination: {
        page: params.page,
        limit: params.limit,
        total: count,
        totalPages,
        hasNextPage: params.page < totalPages,
        hasPreviousPage: params.page > 1
      }
    };

    console.log('getPaginatedRecords: returning result', result);

    return result;
  },

  // NEW: Method to get records with tags included
  async getPaginatedRecordsWithTags(params: PaginationParams): Promise<PaginatedResponse<RecordWithTemplate>> {
    return this.getPaginatedRecords(params, true);
  },

  // NEW: Update record title
  async updateRecordTitle(recordId: string, newTitle: string): Promise<void> {
    const { error } = await supabase
      .from("records")
      .update({ name: newTitle })
      .eq("id", recordId)
      .single();

    if (error) {
      throw error;
    }
  },

  // SHARING: Check if record can be shared (status must be 'completed')
  async canRecordBeShared(recordId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("records")
        .select("status")
        .eq("id", recordId)
        .single();

      if (error) {
        console.error("Error checking record sharing eligibility:", error);
        return false;
      }

      return data?.status === "completed";
    } catch (error) {
      console.error("Error checking record sharing eligibility:", error);
      return false;
    }
  },

  // SHARING: Get record basic info for sharing (without sensitive data)
  async getRecordForSharing(recordId: string): Promise<{
    id: string;
    name: string;
    duration: unknown;
    createdAt: string;
    status: string;
  } | null> {
    try {
      const { data, error } = await supabase
        .from("records")
        .select("id, name, duration, created_at, status")
        .eq("id", recordId)
        .single();

      if (error) {
        console.error("Error fetching record for sharing:", error);
        return null;
      }

      return {
        id: data.id,
        name: data.name,
        duration: data.duration,
        createdAt: data.created_at,
        status: data.status
      };
    } catch (error) {
      console.error("Error fetching record for sharing:", error);
      return null;
    }
  },

  // SHARING: Check if user owns the record
  async isRecordOwner(recordId: string, userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("records")
        .select("user_id")
        .eq("id", recordId)
        .single();

      if (error) {
        console.error("Error checking record ownership:", error);
        return false;
      }

      return data?.user_id === userId;
    } catch (error) {
      console.error("Error checking record ownership:", error);
      return false;
    }
  },

  // NEW: Update record processed text
  async updateRecordProcessedText(recordId: string, newText: string): Promise<void> {
    const { error } = await supabase
      .from("records")
      .update({ post_processing_text: newText })
      .eq("id", recordId)
      .single();

    if (error) {
      throw error;
    }
  },
};
